import React from 'react';
import { useGameStore, selectCurrentLesson, selectCurrentSentence } from '../store/gameStore';
import { useTts } from '../hooks/useTts';

const GameControls = () => {
  const { 
    resetCurrent, 
    shufflePool, 
    checkAnswer, 
    openSettings,
    prevSentence,
    nextSentence,
    currentSentenceIndex,
    feedback
  } = useGameStore();
  const currentLesson = useGameStore(selectCurrentLesson);
  const currentSentence = useGameStore(selectCurrentSentence);
  const { play, loading: ttsLoading, playing: ttsPlaying, error: ttsError } = useTts();
  
  const totalSentences = currentLesson?.dialogue?.length || 0;

  const handleListen = () => {
    if (currentSentence?.chinese) play(currentSentence.chinese);
  };
  
  const buttonStyle = {
    padding: '10px 24px',
    borderRadius: '10px',
    border: 'none',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s, transform 0.1s',
  };
  
  const resetStyle = {
    ...buttonStyle,
    background: '#334155',
    color: '#e2e8f0',
  };
  
  const shuffleStyle = {
    ...buttonStyle,
    background: '#334155',
    color: '#e2e8f0',
  };
  
  const checkStyle = {
    ...buttonStyle,
    background: '#6366f1',
    color: '#fff',
  };
  
  const listenStyle = {
    ...buttonStyle,
    background: '#0e7490',
    color: '#e0f2fe',
    opacity: ttsLoading ? 0.7 : 1,
  };
  
  const navButtonStyle = {
    background: '#1e293b',
    border: '1px solid #334155',
    color: '#e2e8f0',
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    fontSize: '1.2rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s',
  };
  
  const counterStyle = {
    fontSize: '0.9rem',
    color: '#94a3b8',
    minWidth: '60px',
    textAlign: 'center',
  };
  
  const hintStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    fontStyle: 'italic',
  };
  
  const feedbackStyle = {
    textAlign: 'center',
    fontSize: '0.9rem',
    minHeight: '24px',
    fontWeight: 600,
    color: feedback.type === 'success' ? '#4ade80' : 
           feedback.type === 'error' ? '#f87171' : 'inherit',
  };
  
  return (
    <>
      {/* Sentence Navigation */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '16px',
        marginBottom: '20px'
      }}>
        <button 
          style={navButtonStyle}
          onClick={prevSentence}
          onMouseEnter={(e) => e.target.style.background = '#334155'}
          onMouseLeave={(e) => e.target.style.background = '#1e293b'}
        >
          ◀
        </button>
        <span style={counterStyle}>
          {currentSentenceIndex + 1} / {totalSentences}
        </span>
        <span style={hintStyle}>
          {currentLesson?.dialogue?.[currentSentenceIndex]?.english}
        </span>
        <button 
          style={navButtonStyle}
          onClick={nextSentence}
          onMouseEnter={(e) => e.target.style.background = '#334155'}
          onMouseLeave={(e) => e.target.style.background = '#1e293b'}
        >
          ▶
        </button>
      </div>
      
      {/* Feedback */}
      <div style={feedbackStyle}>
        {feedback.message}
        {ttsError && <span style={{ color: '#f87171' }}> 🔊 {ttsError}</span>}
      </div>
      
      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        justifyContent: 'center',
        marginTop: '16px'
      }}>
        <button
          style={resetStyle}
          onClick={resetCurrent}
          onMouseEnter={(e) => e.target.style.background = '#475569'}
          onMouseLeave={(e) => e.target.style.background = '#334155'}
        >
          ↺ Reset
        </button>
        <button
          style={shuffleStyle}
          onClick={shufflePool}
          onMouseEnter={(e) => e.target.style.background = '#475569'}
          onMouseLeave={(e) => e.target.style.background = '#334155'}
        >
          🔀 Shuffle
        </button>
        <button
          style={listenStyle}
          onClick={handleListen}
          disabled={ttsLoading || !currentSentence?.chinese}
          title="Listen to the sentence"
        >
          {ttsLoading ? '⏳ …' : ttsPlaying ? '🔊 Playing' : '🔊 Listen'}
        </button>
        <button
          style={checkStyle}
          onClick={checkAnswer}
          onMouseEnter={(e) => e.target.style.background = '#4f46e5'}
          onMouseLeave={(e) => e.target.style.background = '#6366f1'}
        >
          ✓ Check
        </button>
      </div>
    </>
  );
};

export default GameControls;
